import { motion } from "framer-motion";

/**
 * GodRays - Subtle crepuscular light rays streaming from the top,
 * like sunbeams breaking through clouds.
 * Kept short (55vh) with a large bottom fade so the rays never
 * bleed into the section below.
 */
export const GodRays = () => {
  return (
    <div
      aria-hidden="true"
      data-testid="god-rays-layer"
      className="pointer-events-none absolute inset-x-0 top-0 z-[3] overflow-hidden"
      style={{
        height: "80vh",
        WebkitMaskImage:
          "linear-gradient(to bottom, black 0%, black 45%, transparent 90%)",
        maskImage:
          "linear-gradient(to bottom, black 0%, black 45%, transparent 90%)",
      }}
    >
      {/* Soft top halo — the "sun behind clouds" glow */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          top: "-380px",
          width: "700px",
          height: "700px",
          background:
            "radial-gradient(circle, rgba(253,224,71,0.16) 0%, rgba(212,175,55,0.08) 30%, transparent 60%)",
          filter: "blur(40px)",
        }}
      />

      {/* Rays via SVG polygons — end at y ~= 500 so they never reach bottom */}
      <svg
        className="absolute inset-x-0 top-0 w-full"
        style={{ height: "80vh", mixBlendMode: "screen" }}
        viewBox="0 0 1600 800"
        preserveAspectRatio="xMidYMin slice"
      >
        <defs>
          <linearGradient id="rayGrad" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#FDE047" stopOpacity="0.22" />
            <stop offset="25%" stopColor="#D4AF37" stopOpacity="0.10" />
            <stop offset="80%" stopColor="#D4AF37" stopOpacity="0.01" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="rayGradSharp" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#FDE047" stopOpacity="0.32" />
            <stop offset="35%" stopColor="#FDE047" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
          <filter id="rayBlurSoft" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="24" />
          </filter>
          <filter id="rayBlurFine" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>

        {/* Wide diffuse rays — very soft, tapered by gradient */}
        <motion.g
          filter="url(#rayBlurSoft)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.55, 0.8, 0.55] }}
          transition={{
            duration: 12,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "mirror",
          }}
        >
          <polygon points="740,-40 860,-40 1080,800 480,800" fill="url(#rayGrad)" />
          <polygon points="440,-40 540,-40 220,800 -120,800" fill="url(#rayGrad)" />
          <polygon points="1060,-40 1180,-40 1620,800 1260,800" fill="url(#rayGrad)" />
        </motion.g>

        {/* Narrower "peek-through" beams */}
        <motion.g
          filter="url(#rayBlurFine)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{
            duration: 8,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "mirror",
            delay: 2,
          }}
        >
          <polygon points="790,-20 808,-20 880,800 700,800" fill="url(#rayGradSharp)" />
          <polygon points="620,-20 636,-20 520,800 380,800" fill="url(#rayGradSharp)" />
          <polygon points="990,-20 1006,-20 1120,800 960,800" fill="url(#rayGradSharp)" />
        </motion.g>
      </svg>

      {/* Strong bottom fade — covers the entire lower half so no residue remains */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: "60%",
          background:
            "linear-gradient(to bottom, transparent 0%, #090909 55%, #090909 100%)",
        }}
      />
    </div>
  );
};
